<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import { differenceInDays, format, formatDistance } from 'date-fns';
import { fr } from 'date-fns/locale';
import { formatUTCToLocalTime } from "@/utils/date";
import { InstantSearchParams } from '@/services/instant-search';
import algoliasearch from 'algoliasearch/lite';
import { history } from 'instantsearch.js/es/lib/routers';
import { BarsArrowDownIcon, ChevronDownIcon, BarsArrowUpIcon } from '@heroicons/vue/20/solid';
import { SwitchGroup, Switch, SwitchLabel } from "@headlessui/vue";
import draggable from 'vuedraggable'

import BadgeLegalStep from '@/components/ui/badges/BadgeLegalStep.vue';
import SearchBox from '@/components/search/ais/SearchBox.vue';
import Pagination from "@/components/search/ais/Pagination.vue";
import HeroIcons from '@/components/ui/icons/HeroIcons.vue';
import RefinementList from '@/components/search/ais/RefinementList.vue';
import ClearRefinements from '@/components/search/ais/ClearRefinements.vue';
import SearchAirline from '@/components/search/SearchAirline.vue';
import BadgeClaimType from '@/components/ui/badges/BadgeClaimType.vue';
import FilterState from "./FilterState.vue";

import type { SearchClaimCollection, SearchSignatureDocument } from "@upclaim/sdk/src/data-contracts";
import TabsSearch from './TabsSearch.vue';
import FilterClaimType from './FilterClaimType.vue';
import { useSearchStore } from '@/store';
import RefinementListCard from './ais/RefinementListCard.vue';
import BaseButton from '../ui/buttons/BaseButton.vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { UsersIcon } from '@heroicons/vue/24/solid';
import { ArrowUpRight, ChevronFirstIcon, ChevronLastIcon, ExternalLinkIcon, FolderOpenIcon, FullscreenIcon, ListFilter, PanelRightOpenIcon, ScanSearchIcon, ZoomInIcon } from 'lucide-vue-next';

const route = useRoute();
const props = defineProps({
  searchParams: {
    required: false,
    type: Object as () => InstantSearchParams
  },
  columns: {
    type: Array as () => string[]
  },
  hitsPerPage: {
    default: 20,
    type: Number,
  }
});
const emit = defineEmits(["claimClick"]);


const showFilters = ref(false);
const routing = {
  router: history()
};

const selectedClaimId = ref<string | null>(null);

function handleClick(newlySelectedClaimId: string) {
  // router.push({ name: 'ClaimOverview', params: { id: claim.id } });
  const claimIdToSelect = selectedClaimId.value === newlySelectedClaimId
    ? null /** Un-select */
    : newlySelectedClaimId;

  emit('claimClick', claimIdToSelect);
  selectedClaimId.value = claimIdToSelect
}

// `isLawyer` is a quick fix - In the near future we'll use API generated scoped keys
const isLawyer = Boolean(useAuth0().user?.value?.["https://netlify-integration.com/roles"]?.includes("LAWYER"));
const searchParameters = ref(
  isLawyer ? "claimStates:COURT.WAIT_FOR_JURIDCTION_CHOICE AND (claimStateRemainingDelay < 0 OR claimStateRemainingDelay >= 0)"
   : "NOT claimLegalStepName:CANCELLED AND NOT claimLegalStepName:LOST"
);

function toLocaleUpperCaseFirstChar(str) {
  return str.charAt(1).toLocaleUpperCase() + str.slice(2);
}
function transformItems(items) {
  return items.map(item => ({
    ...item,
    label: item.label.toUpperCase(),
  }));
}

const remainingDelayInDays = (claimStateRemainingDelay) => claimStateRemainingDelay == null
  ? null
  : differenceInDays(new Date(claimStateRemainingDelay), new Date(0));

const client = algoliasearch(
  'X2I38XQM9R',
  useSearchStore().searchKey?.securedApiKey as string
);

function updateTab(filter) {
  searchParameters.value = filter
}

const sortByTicketNumber = ref([
  { value: 'claim', label: 'desc' },
  { value: 'claim:ticketNumber:asc', label: 'asc' }
]);
</script>
<template>
  <div class="flex flex-col mt-8">
    <ais-instant-search :routing="true" :search-client="client" index-name="claim">
      <ais-configure :filters="searchParameters" />

      <TabsSearch @tab-updated="updateTab" class="my-6" />

      <ais-dynamic-widgets :facets="['*']">
        <ais-refinement-list attribute="agent.email" />
        <ais-panel>
          <ais-menu attribute="travelAgencyCompany.displayName" />
        </ais-panel>
        <ais-hierarchical-menu :attributes="['travelAgencyCompany.displayName']" />
      </ais-dynamic-widgets>
      <div class="flex pt-4 pb-8 text-left gap-x-8">
        <RefinementListCard attribute="claimLegalStepName" attribute-name="Étape juridique" size="lg" />
      </div>
      <div>
        <ClearRefinements class="py-4" />
      </div>
      <div id="advanced-filters"></div>
      <div id="CreateClaimGroup"></div>
      <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
          <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <div class="inline-flex items-center w-full p-4 bg-gray-50 gap-x-4">
              <div class="w-full">
                <SearchBox placeholder="Rechercher une claim..." />
              </div>
              <div class="mt-1">
                <BaseButton text="Filtres" size="md" :type="showFilters ? 'submit' : 'primary'" :icon="ListFilter" @click="showFilters = !showFilters" />
              </div>
            </div>

            <div v-show="showFilters" class="flex px-4 text-left border-y border-slate-200 bg-gray-50 gap-x-6">
              <RefinementList attribute="travelAgencyOffice.travelAgency.name" attribute-name="Agence de voyage" />
              <RefinementList attribute="travelAgencyOffice.amadeusOfficeId" attribute-name="Office ID" />
              <RefinementList attribute="agent.email" attribute-name="Agent" size="lg" />
              <RefinementList attribute="travelAgencyCompany.displayName" attribute-name="Entreprise" size="lg" />
              <!-- <div>
                Agence de voyage
                <ais-refinement-list attribute="travelAgencyOffice.travelAgency.name" :show-more="true" operator="or"></ais-refinement-list>
              </div>
              <div>
                Agents
                <ais-refinement-list attribute="agent.email" :show-more="true" operator="or"></ais-refinement-list>
              </div>
              <div>
                Entreprises
                <ais-refinement-list attribute="travelAgencyCompany.displayName" :show-more="true" operator="or"></ais-refinement-list>
              </div>
              <div>
                Office ID
                <ais-refinement-list attribute="travelAgencyOffice.amadeusOfficeId" :show-more="true" operator="or"></ais-refinement-list>
              </div> -->
              <!-- <div>
                B2C ?
                <ais-refinement-list attribute="isB2c" operator="or"></ais-refinement-list>
              </div> -->
            </div>

            <ais-hits>
              <template v-slot="{ items } : { items: SearchClaimCollection }">
                <table class="min-w-full text-left divide-y divide-gray-300">
                  <thead class="text-left bg-gray-50">
                    <tr>
                      <th scope="col"
                        class="py-3.5 inline-flex pl-4 pr-3 text-left text-sm font-semibold text-gray-900 cursor-pointer">
                        <ais-sort-by :items=" sortByTicketNumber ">
                          <template v-slot=" { items: sortItems, refine: refineTickets, currentRefinement } ">
                            <div @click.prevent="refineTickets(currentRefinement === 'claim_ticketNumber_desc' ? 'claim_ticketNumber_asc' : 'claim_ticketNumber_desc')" class="flex">
                              Ticket
                              <span class="flex-none ml-2 text-gray-900 bg-gray-100 rounded cursor-pointer group-hover:bg-gray-200">
                                <BarsArrowDownIcon
                                  v-show=" currentRefinement === 'claim'"
                                  class="w-5 h-5"
                                  aria-hidden="true" />
                                <BarsArrowDownIcon v-show=" currentRefinement === 'claim_ticketNumber_desc' "
                                  class="w-5 h-5"
                                  aria-hidden="true" />
                                <BarsArrowUpIcon v-show=" currentRefinement === 'claim_ticketNumber_asc' "
                                  class="w-5 h-5"
                                  aria-hidden="true" />
                              </span>
                            </div>
                          </template>
                        </ais-sort-by>
                      </th>
                      <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        <ais-sort-by :items=" sortByTicketNumber ">
                          <template v-slot=" { items: sortItems, refine: refineTickets, currentRefinement } ">
                            <div class="flex">
                              <span @click.prevent="refineTickets(currentRefinement === 'claim_flightDeparture_desc' ? 'claim_flightDeparture_asc' : 'claim_flightDeparture_desc')"
                                v-tooltip.bottom="'Trier par date de départ'"
                                class="cursor-pointer"
                              >
                                Vol
                              </span>
                              <span class="flex-none ml-2 text-gray-900 bg-gray-100 rounded cursor-pointer group-hover:bg-gray-200">
                                <BarsArrowDownIcon v-show=" currentRefinement === 'claim_flightDeparture_desc' "
                                  class="w-5 h-5"
                                  aria-hidden="true" />
                                <BarsArrowUpIcon v-show=" currentRefinement === 'claim_flightDeparture_asc' "
                                  class="w-5 h-5"
                                  aria-hidden="true" />
                              </span>
                            </div>
                          </template>
                        </ais-sort-by>
                      </th>
                      <th scope="col" class="py-3.5 text-left text-sm font-semibold text-gray-900">
                        <FilterClaimType />
                      </th>
                      <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 inline-flex">
                        <!-- Étape juridique -->
                        <FilterState />
                      </th>
                      <th scope="col" class="py-3.5 text-left text-sm font-semibold text-gray-900">Assignés</th>
                      <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-6 cursor-pointer"
                        @click="showFilters = !showFilters" :class=" showFilters ? 'text-blue-700' : 'text-gray-800' ">
                        <span class="sr-only">Open</span>
                        <!-- <HeroIcons name="FunnelIcon" class="w-6 h-6" :solid="!!showFilters" /> -->
                      </th>
                    </tr>
                  </thead>
                  <!-- <tbody v-if=" items?.length " class="bg-white divide-y divide-gray-200"> -->



                    <!-- @click="router.push({ name: 'ClaimOverview', params: { id: claim.id } })" -->
                    <draggable v-if=" items?.length " class="bg-white divide-y divide-gray-200" group="claims" :list="items" tag="tbody" item-key="id" role="tbody">
                      <template #item="{ element: claim, index }">

                          <tr @click="handleClick(claim.id)"
                      class="cursor-pointer"
                      :class=" claim.id === selectedClaimId ? 'bg-blue-50 cursor-auto' : 'bg-white hover:bg-zinc-50 hover:border-l-2 border-blue-700' "
                    >
                      <td class="py-4 pl-6 pr-3 text-sm whitespace-nowrap"
                        :class=" claim.id === selectedClaimId ? 'border-l-2 border-blue-800' : ''"
                      >
                        #{{ claim.ticketNumber }}
                      </td>

                      <td class="max-w-xs px-3 py-4 text-sm text-left text-gray-500 text-ellipsis">
                        <div class="flex items-center">
                          <div class="flex-shrink-0 w-10 h-10">
                            <img class="w-10 h-10 rounded-full" :src=" claim.effectiveAirlineIconURL "
                              alt="Airline Logo" />
                          </div>
                          <div class="ml-4 divide-y-2 divide-transparent">
                            <div class="font-medium text-gray-900">{{ claim.effectiveFlightIdentifier }} · {{
                              claim.effectiveAirlineName
                              }}</div>
                            <div class="font-medium text-slate-700">
                              Vol {{ claim.departureAirportIATA }} → {{ claim.arrivalAirportIATA }}
                              (du {{ formatUTCToLocalTime(new Date(claim.flightScheduledDepartureUTC), claim.departureAirportTimezone, "dd/MM/yyyy") }})
                            </div>
                            <div v-if="claim.travelAgencyCompany">
                              <span class="mr-2 text-slate-700">
                                Entreprise : {{
                                  // @ts-ignore
                                  claim.travelAgencyCompany?.displayName
                                }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>

                      <td class="py-4 text-sm text-left text-gray-500 whitespace-nowrap">
                        <div class="block divide-y-2 divide-transparent ">
                          <BadgeClaimType :name="claim.claimTypeName" />
                          <div v-if="claim.passengers?.length" class="pt-1 text-gray-800">
                            <span>
                              {{ claim.passengers[0].firstName }} {{ claim.passengers[0].lastName.toUpperCase() }}
                            </span>
                            <span v-if="claim.passengers.length > 1" v-tooltip.right="{
                              html: true,
                              content: claim.passengers.filter((_, i) => i)
                                .map(p => `${p.firstName} ${p.lastName.toUpperCase()}`).join('<br>'),
                            }">
                              (+ {{ claim.passengers.length - 1 }}pax.)
                            </span>
                          </div>
                          <div class=" text-slate-600">
                            Dossier créé le {{ new Date(claim.createdAt).toLocaleDateString() }}
                          </div>

                          <!-- <div v-if=" claim.lastMessageSentOn">
                            <div v-if=" claim.lastMessageReceivedOn">
                              Dernier email reçu il y a <i>{{ formatDistance(new Date(claim.lastMessageReceivedOn),
                                Date.now(), { locale: fr })}}</i>
                            </div>
                            Dernier email envoyé il y a <i>{{ formatDistance(new Date(claim.lastMessageSentOn),
                              Date.now(), { locale: fr })}}</i>
                          </div>
                          <div v-else>
                            Aucun email envoyé pour l'instant
                          </div> -->
                        </div>
                      </td>

                      <td class="px-3 py-4 text-sm text-left text-gray-500 divide-y-2 divide-transparent whitespace-nowrap" @click.stop="">
                        <BadgeLegalStep :name=" claim.claimLegalStepName " :dot=" true " />
                        <div class="block italic text-gray-800">
                          {{ claim.claimStateDisplayName }}
                          <!-- <span v-else-if="claim.cl" class="">Dossier clôturé</span> -->
                        </div>
                        <div class="text-gray-800">
                          <p v-if="claim.claimStateRemainingDelay > 0"> {{ remainingDelayInDays(claim.claimStateRemainingDelay) }} jours restant avant nouvelle étape</p>
                          <p v-else-if="claim.claimStateRemainingDelay < 0"  v-permission="['UPCLAIM_ADMIN', 'LAWYER']">Expiré depuis <b>{{ Math.abs( remainingDelayInDays(claim.claimStateRemainingDelay) || 1 ) }}</b> jours </p>
                        </div>
                        <div v-if="claim?.claimStateUpdatedAt">
                          Étape mise à jour il y a {{ formatDistance(new Date(claim?.claimStateUpdatedAt), new Date(), { locale: fr }) }}
                        </div>
                        <div v-else>
                          <!-- Attente de validation dossier -->
                          Aucun historique sur ce dossier
                        </div>
                      </td>

                      <td class="py-4 text-sm text-center text-gray-500 whitespace-nowrap">
                        <div class="flex -space-x-2 overflow-hidden text-center">
                          <img class="inline-block w-8 h-8 rounded-full ring-2 ring-white"
                            v-tooltip=" claim.assignedAdmin ? `${claim.assignedAdmin?.fullName}` : null "
                            :src=" claim.assignedAdmin?.profilePictureUrl " alt="" />
                        </div>
                      </td>

                      <td class="relative py-4 pl-3 pr-4 text-sm font-medium text-right whitespace-nowrap sm:pr-6">
                        <router-link :to=" { name: 'ClaimOverview', params: { id: claim.id } } "
                          class="inline-flex items-center justify-center gap-x-1.5 rounded-md px-3 py-2 text-sm font-semibold shadow-sm disabled:cursor-not-allowed disabled:opacity-40 text-gray-900 bg-white ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:hover:bg-white"
                        >
                          <!-- <ArrowUpRight class="-ml-0.5 w-4 h-4 text-slate-500" /> -->
                          <span>Ouvrir <span class="sr-only">, {{ claim.ticketNumber }}</span></span>
                        </router-link>
                      </td>
                    </tr>


                      </template>
                    </draggable>
                  <!-- </tbody> -->
                </table>
                <!-- Empty state -->
                <div class="w-full">
                  <div v-show=" !items?.length " colspan="2"
                    class="flex items-center min-w-full text-center border rounded-lg h-96">
                    <div class="flex flex-col w-full max-w-sm px-4 mx-auto">
                      <div class="p-3 mx-auto text-blue-500 bg-blue-100 rounded-full">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                          stroke="currentColor" class="w-6 h-6">
                          <path stroke-linecap="round" stroke-linejoin="round"
                            d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                        </svg>
                      </div>
                      <h1 class="mt-3 text-lg text-gray-800">Aucun dossier</h1>
                      <p class="mt-2 text-sm text-gray-500">Votre recherche ne correspond à aucune claim. Veuillez réessayer ou
                        ajouter une nouvelle claim.</p>
                      <div v-if="false" class="flex items-center mt-4 sm:mx-auto gap-x-3">
                        <ais-clear-refinements>
                          <template v-slot=" { canRefine, refine, createURL } ">
                            <button
                              class="w-1/2 px-5 py-2 text-sm text-gray-700 transition-colors duration-200 bg-white border rounded-lg sm:w-auto hover:bg-gray-100"
                              :href=" createURL() " @click.prevent=" refine " v-if=" canRefine ">
                              Effacer la recherche
                            </button>
                          </template>
                        </ais-clear-refinements>


                        <button
                          class="flex items-center justify-center w-1/2 px-5 py-2 text-sm tracking-wide text-white transition-colors duration-200 bg-blue-500 rounded-lg shrink-0 sm:w-auto gap-x-2 hover:bg-blue-600">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                            stroke="currentColor" class="w-5 h-5">
                            <path stroke-linecap="round" stroke-linejoin="round"
                              d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>

                          <span>Ajouter une claim</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </ais-hits>

          </div>
        </div>
      </div>

      <!-- <ClearRefinements /> -->
      <!-- <ais-configure :attributesToSnippet="['description:50']" snippetEllipsisText="…" class="mt-64" /> -->
      <div class="relative py-10">
        <div class="">


          <!-- <div v-if="showFilters === true" class="px-4 py-20">
            <ClearRefinements />
            <SearchAirline />
            <RefinementList attribute="departureAirportName" attribute-name="Aéroport de départ" />
            <RefinementList attribute="arrivalAirportName" attribute-name="Aéroport d'arrivée" />
            <RefinementList attribute="assignedAdminEmail" attribute-name="Email de l'avocat en charge du dossier" />
            <RefinementList attribute="effectiveAirlineName" attribute-name="Nom de la compagnie aérienne effective" />
            <RefinementList attribute="numberOfPassengers" attribute-name="Nombre de passagers" />
            ec261CompensationAmount
          </div> -->

          <Pagination :page-size=" hitsPerPage " />
        </div>
      </div>
    </ais-instant-search>
  </div>
</template>

<style>
.ais-SearchBox {
  @apply p-4;
}

.ais-SearchBox-input {
  @apply mt-4;
  width: 100%;
}

.ais-SearchBox-reset {
  display: none;
}
</style>